import React, { useEffect, useState, useCallback, useRef } from "react";
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from "react-redux";
import allActions from "../../../actions";

import {
    Typography,
    Paper,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
} from "@material-ui/core";

import { Alert } from '@material-ui/lab';

import MyTable from '../../utils/MyTable';
import RateCell, { RateStatusIcon, ManualRateIcon } from '../../utils/RateCell';
import { checkUserIsRole, roles } from '../../utils/user.js';

import { unoTheme } from '../../../assets/styles/unoTheme';
import { applyMaskAmount, applyMaskCnpj, getDayMonthYearByPtDate, justNumbers } from '../../utils/utils';
import {
    RATE_STATUS,
    RATE_STATUS_LABEL,
    MANUAL_RATE_LABEL,
    buildFundRateRow,
} from '../../utils/costsUtils';

import { FundAPI } from '../../../API/fund';
import { ComDinheiroAPI } from '../../../API/comdinheiro-api';
import { InstitutionRatesAPI } from '../../../API/institution_rates';
import { ClientContractValuesAPI } from '../../../API/client_contract_values';
import { ClientFundRatesAPI } from '../../../API/client_fund_rates';
import { buildContractCostsByMonth } from '../../utils/contractValues';
import { getCodeToComdinheiroQuotas } from "../../../controllers/FundController";


const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
    },
    main: {
        //padding: '0px 18px 16px 18px',
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: '14px',
        color: 'white',
        backgroundColor: unoTheme.mainColor,
        textAlign: 'center',
        padding: '8px 16px',
        borderRadius: '10px 10px 0 0',
        marginBottom: '0px',
    },
    tablePaper: {
        borderRadius: '0 0 10px 10px',
        overflow: 'hidden',
        marginBottom: '8px',
    },
    selectableRow: {
        cursor: 'pointer',
        '& > *': {
            borderBottom: 'unset',
        },
        '&:nth-of-type(odd)': {
            backgroundColor: unoTheme.secondaryTuplaTable,
        },
        '&:hover': {
            backgroundColor: unoTheme.hoverTable,
        },
    },
    emptyRow: {
        '& td': {
            padding: '16px',
            color: '#999',
            fontStyle: 'italic',
        },
    },
    topAlert: {
        fontSize: '12px',
        borderRadius: '0px',
        // O texto ocupa várias linhas: com 'center' o ícone flutuaria no meio do bloco.
        alignItems: 'flex-start',
        '& .MuiAlert-message': {
            padding: '6px 0px',
            lineHeight: 1.5,
        },
    },
    fundNameCell: {
        lineHeight: '1.3',
    },
    fundName: {
        display: 'block',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    fundCnpj: {
        display: 'block',
        fontSize: '11px',
        color: '#666',
    },
    statusLegend: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        fontSize: '11px',
        color: '#666',
        marginTop: '6px',
        marginBottom: '10px',
        paddingLeft: '4px',
    },
    statusLegendItem: {
        display: 'flex',
        alignItems: 'center',
        marginRight: '16px',
        '& > * + *': {
            marginLeft: '4px',
        },
    },
    methodology: {
        borderRadius: '10px',
        padding: '16px 20px',
        marginTop: '16px',
        marginBottom: '16px',
    },
    methodologyTitle: {
        fontWeight: 600,
        fontSize: '13px',
        marginBottom: '10px',
        textAlign: 'left',
        color: unoTheme.mainColor,
    },
    methodologyItem: {
        fontSize: '11.5px',
        color: '#555',
        lineHeight: '1.6',
        textAlign: 'left',
        marginBottom: '8px',
    },
    methodologyTerm: {
        fontWeight: 600,
        color: '#333',
    },
    performanceRuleCell: {
        fontSize: '11.5px',
        lineHeight: '1.3',
    },
    // Cor por grupo nos headers da tabela de fundos.
    //
    // O MyTable pinta TODAS as células de header com `background: white` através de
    // '& .MuiTableRow-head & .MuiTableCell-root' (MyTable.js), que compila com especificidade
    // 0,3,0 — uma classe simples na TableCell (0,1,0) perderia. Por isso as regras ficam
    // aninhadas sob esta classe, aplicada no <TableHead>: o seletor resultante tem 4 classes
    // (0,4,0) e vence sem precisar de !important nem de alterar o MyTable.
    //
    // As células recebem o nome da classe como string literal, mesmo idioma que o MyTable já
    // usa para 'highlighted' / 'smallPadding' / 'mainColor'.
    fundsTableHead: {
        '& .MuiTableRow-head .MuiTableCell-root.costsGroupBlue': {
            background: colorsCosts.blue2,
            color: colorsCosts.blueLabel,
        },
        '& .MuiTableRow-head .MuiTableCell-root.costsSubBlue': {
            background: colorsCosts.blue1,
            color: colorsCosts.blueLabel,
        },
        '& .MuiTableRow-head .MuiTableCell-root.costsGroupGreen': {
            background: colorsCosts.green1,
            color: colorsCosts.greenLabel,
        },
        '& .MuiTableRow-head .MuiTableCell-root.costsSubGreen': {
            background: colorsCosts.green2,
            color: colorsCosts.greenLabel,
        },
        '& .MuiTableRow-head .MuiTableCell-root.costsGroupPurple': {
            background: colorsCosts.purple1,
            color: colorsCosts.purpleLabel,
        },
        '& .MuiTableRow-head .MuiTableCell-root.costsSubPurple': {
            background: colorsCosts.purple2,
            color: colorsCosts.purpleLabel,
        },
    },
}));

// Retorna início e fim do período no formato DD/MM/YYYY exigido pela ComDinheiro.
// No mensal, subPeriod é o número do mês (1-12); no trimestral/semestral, o número do trimestre/semestre.
export function getPeriodDates(year, periodType, subPeriod) {
    if (periodType === 'mensal') {
        const month = moment.utc({ year: year, month: subPeriod - 1, day: 1 });
        return {
            startDate: month.format('DD/MM/YYYY'),
            endDate: month.clone().endOf('month').format('DD/MM/YYYY')
        };
    }

    if (periodType === 'anual') {
        return {
            startDate: `01/01/${year}`,
            endDate: `31/12/${year}`
        };
    }

    if (periodType === 'semestral') {
        return subPeriod === 1
            ? {
                startDate: `01/01/${year}`,
                endDate: `30/06/${year}`
            }
            : {
                startDate: `01/07/${year}`,
                endDate: `31/12/${year}`
            };
    }

    // trimestral
    const quarters = {
        1: {
            startDate: `01/01/${year}`,
            endDate: `31/03/${year}`
        },
        2: {
            startDate: `01/04/${year}`,
            endDate: `30/06/${year}`
        },
        3: {
            startDate: `01/07/${year}`,
            endDate: `30/09/${year}`
        },
        4: {
            startDate: `01/10/${year}`,
            endDate: `31/12/${year}`
        }
    };

    return quarters[subPeriod];
}

const colorsCosts = {
    blue2: '#dae9ff',
    blue1: '#f1f5fe',
    blueLabel: unoTheme.mainColor,
    green1: '#e5f0e7',
    green2: '#f4f9f5',
    greenLabel: '#53755a',
    purple1: '#eae2fa',
    purple2: '#f7f1fd',
    purpleLabel: '#654f77'
};

export default function CostsScreen({ selectedYear, periodType, subPeriod, onDataLoaded }) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const clientOn = useSelector(state => state.clientOn);
    const currentUser = useSelector(state => state.currentUser);
    const rightContent = useSelector(state => state.rightContent);

    //Ajuste manual de taxas de fundo só pelo pessoal da LEMA; o servidor aplica a mesma restrição
    const canEditFundRates = checkUserIsRole(currentUser, [roles.superadmin, roles.admin, roles.advisor]);

    // Dados das 3 tabelas
    const [fundsData, setFundsData] = useState([]);
    const [institutionData, setInstitutionData] = useState([]);
    const [otherCostsData, setOtherCostsData] = useState([]);

    const [loadingFunds, setLoadingFunds] = useState(false);
    const [loadingInstitutions, setLoadingInstitutions] = useState(false);
    const [loadingOtherCosts, setLoadingOtherCosts] = useState(false);

    //Insumos da última montagem da tabela de fundos (resposta da ComDinheiro + ajustes manuais):
    //depois de um ajuste as linhas são remontadas com eles, sem consultar a ComDinheiro de novo
    const fundsSourceRef = useRef(null);
    //Último pacote entregue ao onDataLoaded (PDF), para reenviar só a parte que mudou
    const loadedDataRef = useRef(null);

    //Só a troca de cliente recarrega: um setClient que não troca o cliente (ex.: selects globais
    //de mês/ano) cria um clientOn novo e, com [clientOn], refazia toda a consulta à ComDinheiro
    const clientId = clientOn && clientOn.client ? clientOn.client.id : null;

    // Carrega dados quando muda cliente, ano, período ou sub-período
    const loadCostsData = useCallback(async () => {
        if (!clientOn || !clientOn.client) return;
        const clientId = clientOn.client.id;
        const { startDate, endDate } = getPeriodDates(selectedYear, periodType, subPeriod);

        try {
            const [funds, institutions, otherCosts] = await Promise.all([
                loadFundsData(clientId, startDate, endDate),
                loadInstitutionData(clientId, endDate),
                loadOtherCosts(clientId, startDate, endDate),
            ]);
            emitLoadedData({ funds, institutions, otherCosts, startDate, endDate });
        } catch (err) {
            console.error('Erro ao carregar custos:', err);
        }
    }, [clientId, selectedYear, periodType, subPeriod]);

    function emitLoadedData(data) {
        loadedDataRef.current = data;
        if (onDataLoaded) onDataLoaded(data);
    }

    //Ajustes manuais do cliente, por fundo. Falha não bloqueia a tabela: segue só com a fonte.
    async function loadManualRates(clientId) {
        try {
            const response = await ClientFundRatesAPI.listByClient(clientId);
            if (!response.success) {
                console.warn('Não foi possível buscar ajustes manuais de taxas:', response.body);
                return {};
            }
            const manualByFund = {};
            (response.body?.rows ?? []).forEach(row => {
                manualByFund[row.fund_id] = row;
            });
            return manualByFund;
        } catch (e) {
            console.warn('Não foi possível buscar ajustes manuais de taxas:', e);
            return {};
        }
    }

    function buildFundsRows({ uniqueAssets, taxData, apiFailed, month, year, manualByFund }) {
        return uniqueAssets.map(asset => {
            // ComDinheiro retorna chaves com CNPJ formatado (XX.XXX.XXX/XXXX-XX)
            const cnpjClean = justNumbers(asset.cnpj || '');
            const cnpjFormatted = cnpjClean.length === 14 ? applyMaskCnpj(cnpjClean) : asset.cnpj;
            const codeComdinheiro = getCodeToComdinheiroQuotas(asset, month, year);
            const fundInfo = taxData[cnpjFormatted] || taxData[asset.cnpj] || taxData[cnpjClean] || taxData[codeComdinheiro];

            return buildFundRateRow(asset, fundInfo, {
                found: !!fundInfo,
                apiFailed: apiFailed,
            }, manualByFund[asset.fund_id]);
        });
    }

    //Ajuste salvo no painel: aplica a linha gravada (ou a remoção) e remonta a tabela na hora,
    //com a mesma resposta da fonte
    function applyManualRates(fundId, manualRates) {
        const fundsSource = fundsSourceRef.current;
        if (!fundsSource) return;

        const manualByFund = { ...fundsSource.manualByFund };
        if (manualRates) {
            manualByFund[fundId] = manualRates;
        } else {
            delete manualByFund[fundId];
        }
        fundsSource.manualByFund = manualByFund;

        const formatted = buildFundsRows(fundsSource);
        setFundsData(formatted);
        if (loadedDataRef.current) {
            emitLoadedData({ ...loadedDataRef.current, funds: formatted });
        }
    }

    async function reloadInstitutions() {
        if (!clientOn || !clientOn.client || !loadedDataRef.current) return;
        const institutions = await loadInstitutionData(clientOn.client.id, loadedDataRef.current.endDate);
        emitLoadedData({ ...loadedDataRef.current, institutions });
    }

    async function loadFundsData(clientId, startDate, endDate) {
        setLoadingFunds(true);
        fundsSourceRef.current = null;
        try {
            const responseAssets = await FundAPI.getAllClientAssets(clientId, startDate, endDate);
            if (!responseAssets.success || !responseAssets.body || !responseAssets.body.rows) {
                setFundsData([]);
                return [];
            }

            console.log("responseAssets costs: ", responseAssets.body.rows);

            const assets = responseAssets.body.rows;

            // Deduplicar por CNPJ — mesma aplicação pode aparecer em múltiplas contas/datas
            const uniqueMap = {};
            assets.forEach(a => {
                if (a.cnpj && !uniqueMap[a.cnpj]) {
                    uniqueMap[a.cnpj] = a;
                }
            });
            const uniqueAssets = Object.values(uniqueMap);
            const cnpjs = uniqueAssets.map(a => a.cnpj).filter(Boolean);

            if (cnpjs.length === 0) {
                setFundsData([]);
                return [];
            }

            // Buscar informações de taxas via ComDinheiro
            let taxData = {};
            // Uma falha total da API é diferente de "o fundo não divulgou a taxa": esse
            // booleano preserva a distinção até a resolução do status.
            let apiFailed = false;

            async function loadComdinheiroTaxes() {
                try {
                    const comdinheiroResponse = await ComDinheiroAPI.getFundCostsByDate(
                        endDate, uniqueAssets
                    );
                    if (comdinheiroResponse.success && comdinheiroResponse.body) {
                        taxData = comdinheiroResponse.body;
                    } else {
                        apiFailed = true;
                    }
                } catch (e) {
                    apiFailed = true;
                    console.warn('Não foi possível buscar taxas ComDinheiro:', e);
                }
            }

            // Ajustes manuais (client_fund_rates) prevalecem sobre a ComDinheiro, taxa a taxa
            const [, manualByFund] = await Promise.all([
                loadComdinheiroTaxes(),
                loadManualRates(clientId),
            ]);

            console.log("taxData: ", taxData);

            // A URL da requisição é montada por normalizeCnpjsToUrlCotas passando mês/ano,
            // então a chave precisa ser gerada com os mesmos parâmetros — sem eles a branch
            // de subclasse não dispara e o lookup não casa para fundos com subclasse.
            const { month, year } = getDayMonthYearByPtDate(endDate);

            const fundsSource = { clientId, uniqueAssets, taxData, apiFailed, month, year, manualByFund };
            fundsSourceRef.current = fundsSource;

            const formatted = buildFundsRows(fundsSource);

            setFundsData(formatted);
            return formatted;
        } catch (err) {
            console.warn('Erro ao carregar fundos:', err);
            setFundsData([]);
            return [];
        } finally {
            setLoadingFunds(false);
        }
    }

    async function loadInstitutionData(clientId, endDate) {
        setLoadingInstitutions(true);
        try {
            // Envia referenceDate para suportar R3 (vigência histórica) quando o backend estiver pronto
            const response = await InstitutionRatesAPI.getByClient(clientId, endDate);
            if (response.success) {
                const rows = response.body?.rows ?? response.body ?? [];
                setInstitutionData(rows);
                return rows;
            }
        } catch (err) {
            console.warn('Erro ao carregar taxas de instituições (backend não disponível):', err);
        } finally {
            setLoadingInstitutions(false);
        }
        setInstitutionData([]);
        return [];
    }

    // Custo do contrato mês a mês, pelo histórico de valores (client_contract_values): um reajuste
    // no meio do período vira uma linha por valor, e meses antes do primeiro valor não entram
    async function loadOtherCosts(clientId, startDate, endDate) {
        setLoadingOtherCosts(true);
        try {
            const response = await ClientContractValuesAPI.listByClient(clientId);
            const rows = response.success && response.body ? response.body.rows : [];
            const segments = buildContractCostsByMonth(rows, startDate, endDate);

            const data = segments.map(segment => ({
                description: segments.length > 1
                    ? `Consultoria / Lema (${segment.from === segment.to ? segment.from : segment.from + ' a ' + segment.to})`
                    : 'Consultoria / Lema',
                monthly_value: segment.monthly_value,
                months: segment.months,
                total: segment.total,
            }));
            setOtherCostsData(data);
            return data;
        } catch (err) {
            console.warn('Erro ao carregar valores de contrato:', err);
            setOtherCostsData([]);
            return [];
        } finally {
            setLoadingOtherCosts(false);
        }
    }

    useEffect(() => {
        if (clientOn && clientOn.client && selectedYear && periodType) {
            loadCostsData();
        }
    }, [clientId, selectedYear, periodType, subPeriod]);

    useEffect(() => {
        if (!rightContent || !rightContent.action) return;

        //Painel de taxas do fundo salvou: atualiza a tabela já, com o painel ainda aberto
        if (rightContent.action.task === 'updateFundRates') {
            applyManualRates(rightContent.action.data.fundId, rightContent.action.data.manualRates);
            return;
        }

        //Painel de taxas da instituição fechado depois de uma gravação: recarrega as instituições e
        //limpa o {action: 'update'}, que de outro modo deixaria o drawer aberto e vazio
        if (rightContent.action === 'update') {
            reloadInstitutions();
            dispatch(allActions.rightContentActions.setRightContent(null));
        }
    }, [rightContent]);

    function handleFundClick(row) {
        dispatch(allActions.rightContentActions.setRightContent({
            path: 'clientFundRates',
            clientOnScreen: clientOn.client,
            data: row,
        }));
    }

    function handleInstitutionClick(row) {
        dispatch(allActions.rightContentActions.setRightContent({
            path: 'institutionRateHistory',
            clientOnScreen: clientOn.client,
            data: row,
        }));
    }

    // Célula da Regra da Performance. Mostra o benchmark resolvido; o separador e as
    // condições só aparecem quando houver condições cadastradas — hoje não há campo para
    // elas em nenhuma fonte, então nada é preenchido por inferência.
    function renderPerformanceRuleCell(rule) {
        if (!rule || rule.status !== RATE_STATUS.INFORMED) {
            return <RateCell rate={rule} />;
        }

        const title = rule.conditions
            ? `Benchmark: ${rule.benchmark} | ${rule.conditions}`
            : `Benchmark: ${rule.benchmark}`;

        return (
            <TableCell align="center" className={classes.performanceRuleCell} title={title}>
                {rule.benchmark}
                {rule.conditions ? ` | ${rule.conditions}` : null}
            </TableCell>
        );
    }

    // Formata percentual das tabelas de Instituições. Se showZero=false (padrão), valores 0
    // exibem "-". A tabela de Fundos usa formatRate/RateCell, que nunca esconde o zero.
    function formatPercent(value, showZero = false) {
        if (value === '-' || value === null || value === undefined) return '-';
        const num = parseFloat(value);
        if (isNaN(num)) return '-';
        if (num === 0 && !showZero) return '-';
        return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
    }

    return (
        <div className={classes.root}>
            <div className={'defaultScrollWithHeaderContent'} style={{ overflowY: 'auto', paddingBottom: '16px', background: "none" }}>
                <div className={classes.main}>
                    {/* TABELA 1 - Fundos */}
                    <Typography className={classes.sectionTitle}>
                        Fundos
                    </Typography>
                    {/* Relação entre as taxas — o risco maior é o usuário somá-las, inclusive somar a
                        performance, que sequer integra a Taxa Global */}
                    <Alert severity="info" className={classes.topAlert}>
                        A <strong>Taxa Global</strong> e as <strong>taxas segregadas</strong> não devem ser somadas,
                        pois a Taxa Global corresponde à remuneração consolidada, enquanto as taxas segregadas
                        detalham os componentes que a integram. A <strong>Taxa de Performance</strong> possui
                        natureza variável, não integra a Taxa Global e deve ser analisada em conjunto com o
                        benchmark e as demais condições previstas para sua cobrança.
                    </Alert>

                    <Paper className={classes.tablePaper}>
                        <MyTable stickyHeader size="small" sizeheaders="small">
                            <colgroup>
                                <col width="26%" />
                                <col width="10%" />
                                <col width="9%" />
                                <col width="9%" />
                                <col width="11%" />
                                <col width="9%" />
                                <col width="10%" />
                                <col width="16%" />
                            </colgroup>
                            <TableHead className={classes.fundsTableHead}>
                                <TableRow className="smallPadding">
                                    {/* Fundo fica fora dos três grupos e permanece neutro */}
                                    <TableCell align="left" rowSpan={2}>Fundo</TableCell>
                                    <TableCell align="center" className={'costsGroupBlue'}>TAXA CONSOLIDADA</TableCell>
                                    <TableCell align="center" colSpan={4} className={'costsGroupGreen'}>TAXAS SEGREGADAS (QUANDO DIVULGADAS)</TableCell>
                                    <TableCell align="center" colSpan={2} className={'costsGroupPurple'}>REMUNERAÇÃO VARIÁVEL</TableCell>
                                </TableRow>
                                <TableRow className="smallPadding">
                                    <TableCell align="center" className={'costsSubBlue'}>Tx. Global a.a. (%)</TableCell>
                                    <TableCell align="center" className={'costsSubGreen'}>Tx. Adm a.a. (%)</TableCell>
                                    <TableCell align="center" className={'costsSubGreen'}>Tx. Gestão a.a. (%)</TableCell>
                                    <TableCell align="center" className={'costsSubGreen'}>Tx. Máx. Distribuição a.a. (%)</TableCell>
                                    <TableCell align="center" className={'costsSubGreen'}>Tx. Custódia a.a. (%)</TableCell>
                                    <TableCell align="center" className={'costsSubPurple'}>Tx. Performance (%)</TableCell>
                                    <TableCell align="center" className={'costsSubPurple'}>Regra da Performance</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loadingFunds ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <CircularProgress size={24} />
                                        </TableCell>
                                    </TableRow>
                                ) : fundsData.length > 0 ? fundsData.map((row, index) => (
                                    <TableRow
                                        key={'fund-cost-' + index}
                                        className={canEditFundRates && row.fund_id ? classes.selectableRow : undefined}
                                        title={canEditFundRates && row.fund_id ? 'Clique para ajustar as taxas manualmente' : undefined}
                                        onClick={canEditFundRates && row.fund_id ? () => handleFundClick(row) : undefined}>
                                        <TableCell align="left" className={classes.fundNameCell}>
                                            <span className={classes.fundName} title={row.fund_name}>{row.fund_name}</span>
                                            <span className={classes.fundCnpj}>{row.cnpj}</span>
                                        </TableCell>
                                        <RateCell rate={row.rates.global} />
                                        <RateCell rate={row.rates.administration} />
                                        <RateCell rate={row.rates.management} />
                                        <RateCell rate={row.rates.distribution} />
                                        <RateCell rate={row.rates.custody} />
                                        <RateCell rate={row.rates.performance} />
                                        {renderPerformanceRuleCell(row.performanceRule)}
                                    </TableRow>
                                )) : (
                                    <TableRow className={classes.emptyRow}>
                                        <TableCell colSpan={8} align="center">Nenhum fundo encontrado na carteira</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </MyTable>
                    </Paper>

                    {/* Legenda dos status das taxas */}
                    <div className={classes.statusLegend}>
                        {[RATE_STATUS.INFORMED, RATE_STATUS.NOT_INFORMED, RATE_STATUS.NOT_APPLICABLE].map(status => (
                            <span key={'rate-status-' + status} className={classes.statusLegendItem}>
                                <RateStatusIcon status={status} />
                                <span>{RATE_STATUS_LABEL[status]}</span>
                            </span>
                        ))}
                        <span className={classes.statusLegendItem}>
                            <ManualRateIcon />
                            <span>{MANUAL_RATE_LABEL}</span>
                        </span>
                    </div>

                    {/* TABELA 2 - Instituições */}
                    <Typography className={classes.sectionTitle}>
                        Instituições
                    </Typography>
                    <Paper className={classes.tablePaper}>
                        <MyTable stickyHeader size="small" sizeheaders="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left">Instituição</TableCell>
                                    <TableCell align="left">CNPJ</TableCell>
                                    <TableCell align="center">Tx. Custódia (%)</TableCell>
                                    <TableCell align="center">Corretagem (%)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loadingInstitutions ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            <CircularProgress size={24} />
                                        </TableCell>
                                    </TableRow>
                                ) : institutionData.length > 0 ? institutionData.map((row, index) => (
                                    <TableRow
                                        key={'inst-cost-' + index}
                                        className={classes.selectableRow}
                                        onClick={() => handleInstitutionClick(row)}>
                                        <TableCell align="left">{row.instituition_name || '-'}</TableCell>
                                        <TableCell align="left">{row.cnpj || '-'}</TableCell>
                                        <TableCell align="center">{formatPercent(row.custody_rate)}</TableCell>
                                        <TableCell align="center">{formatPercent(row.brokerage_rate)}</TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow className={classes.emptyRow}>
                                        <TableCell colSpan={4} align="center">Nenhuma instituição cadastrada</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </MyTable>
                    </Paper>

                    {/* TABELA 3 - Outros Custos */}
                    <Typography className={classes.sectionTitle}>
                        Outros Custos
                    </Typography>
                    <Paper className={classes.tablePaper}>
                        <MyTable stickyHeader size="small" sizeheaders="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left">Descrição</TableCell>
                                    <TableCell align="center">Valor Mensal (R$)</TableCell>
                                    <TableCell align="center">Meses no Período</TableCell>
                                    <TableCell align="center">Total do Período (R$)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loadingOtherCosts ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            <CircularProgress size={24} />
                                        </TableCell>
                                    </TableRow>
                                ) : otherCostsData.length > 0 ? otherCostsData.map((row, index) => (
                                    <TableRow key={'other-cost-' + index}>
                                        <TableCell align="left">{row.description}</TableCell>
                                        <TableCell align="center">{applyMaskAmount(row.monthly_value, true)}</TableCell>
                                        <TableCell align="center">{row.months}</TableCell>
                                        <TableCell align="center">{applyMaskAmount(row.total, true)}</TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow className={classes.emptyRow}>
                                        <TableCell colSpan={4} align="center">Valor do contrato não cadastrado para este cliente</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </MyTable>
                    </Paper>

                    {/* Observações metodológicas — explicações concentradas em um único bloco */}
                    <Paper className={classes.methodology}>
                        <Typography className={classes.methodologyTitle}>
                            Observações metodológicas
                        </Typography>

                        <Typography className={classes.methodologyItem}>
                            <span className={classes.methodologyTerm}>Taxa Global: </span>
                            soma consolidada das taxas de administração, gestão e máxima de distribuição e, quando
                            aplicável, da taxa de estruturação de previdência. Essa taxa pode ou não ser indicada
                            nos documentos dos fundos.
                        </Typography>

                        <Typography className={classes.methodologyItem}>
                            <span className={classes.methodologyTerm}>Taxa de Administração: </span>
                            remuneração do administrador e dos prestadores de serviços por ele contratados.
                        </Typography>

                        <Typography className={classes.methodologyItem}>
                            <span className={classes.methodologyTerm}>Taxa de Gestão: </span>
                            remuneração do gestor e dos prestadores de serviços por ele contratados.
                        </Typography>

                        <Typography className={classes.methodologyItem}>
                            <span className={classes.methodologyTerm}>Taxa Máxima de Distribuição: </span>
                            limite da remuneração total dos distribuidores, cobrada do fundo e expressa como
                            percentual anual do patrimônio líquido. Taxa vinculada ao percentual da taxa de gestão.
                        </Typography>

                        <Typography className={classes.methodologyItem}>
                            <span className={classes.methodologyTerm}>Taxa de Custódia: </span>
                            remuneração associada aos serviços de custódia dos ativos, cujas despesas podem
                            constituir encargo do fundo.
                        </Typography>

                        <Typography className={classes.methodologyItem}>
                            <span className={classes.methodologyTerm}>Taxa de Performance: </span>
                            remuneração variável vinculada ao desempenho do fundo em relação ao índice de
                            referência, conforme as condições previstas no regulamento.
                        </Typography>

                        <Typography className={classes.methodologyItem}>
                            <span className={classes.methodologyTerm}>Regra da Performance: </span>
                            conjunto de critérios que define a cobrança da taxa de performance, incluindo índice
                            de referência, percentual, periodicidade e cota-base.
                        </Typography>

                        <Typography className={classes.methodologyItem}>
                            <span className={classes.methodologyTerm}>Taxas de Instituições: </span>
                            custódia e corretagem contratadas pelo cliente junto à instituição financeira. São
                            custos distintos das taxas do fundo e não se somam a elas.
                        </Typography>

                        <Typography className={classes.methodologyItem}>
                            <span className={classes.methodologyTerm}>Status das informações: </span>
                            as taxas exibidas refletem o que a fonte de dados divulgou na data de referência do
                            período selecionado. Uma taxa marcada como não informada não significa taxa igual a
                            zero; taxas iguais a zero são exibidas como 0,00%.
                        </Typography>

                        <Typography className={classes.methodologyItem}>
                            <span className={classes.methodologyTerm}>Taxas ajustadas manualmente: </span>
                            taxas revisadas pela consultoria para este cliente prevalecem sobre as informadas pela
                            fonte de dados, em qualquer período.
                        </Typography>
                    </Paper>
                </div>
            </div>
        </div>
    );
}
